import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { BoxArrowUpRight } from 'react-bootstrap-icons';
import SidebarFilterProvider from 'contexts/SidebarFilterContext';
import InputSidebar from 'components/Sidebar/Sidebar';
import Map from 'components/Map/Map';
import BleachingObservationModal from 'components/Bleaching/BleachingObservationModal';
import ProjectBleachingSummary from 'components/Bleaching/ProjectBleachingSummary';
import useBleachingMapData from 'hooks/useBleachingMapData';
import useExpeditionTrack from 'hooks/useExpeditionTrack';
import {
  getMapLayerSettings,
  persistMapLayerSettings,
} from 'utils/mapLayerSettings';
import { pickDefaultMonth } from 'utils/cortadLayers';
import { buildGlobalMapLink } from 'utils/coloniesMapView';
import './ProjectMapPanel.css';

const PROJECT_MAP_SETTINGS_KEY = 'projectMapLayerSettings';

const ProjectMapPanel = ({ colonies = [], projectName = '', projectId = null }) => {
  const initialSettings = getMapLayerSettings(PROJECT_MAP_SETTINGS_KEY);
  const [basemap, setBasemap] = useState(initialSettings.basemap);
  const [captionsVisible, setCaptionsVisible] = useState(initialSettings.captionsVisible);
  const [benthicVisible, setBenthicVisible] = useState(initialSettings.benthicVisible);
  const [benthicClasses, setBenthicClasses] = useState(initialSettings.benthicClasses);
  const [reefExtentVisible, setReefExtentVisible] = useState(initialSettings.reefExtentVisible);
  const [reefExtentClasses, setReefExtentClasses] = useState(initialSettings.reefExtentClasses);
  const [bleachingVisible, setBleachingVisible] = useState(initialSettings.bleachingVisible);
  const [expeditionTrackVisible, setExpeditionTrackVisible] = useState(
    initialSettings.expeditionTrackVisible ?? true,
  );
  const [selectedBleachingObs, setSelectedBleachingObs] = useState(null);
  const [cortadVisible, setCortadVisible] = useState(initialSettings.cortadVisible);
  const [cortadVariable, setCortadVariable] = useState(initialSettings.cortadVariable);
  const [cortadMonth, setCortadMonth] = useState(initialSettings.cortadMonth);
  const [cortadMonths, setCortadMonths] = useState([]);
  const [cortadMeta, setCortadMeta] = useState(null);
  const [cortadBuilding, setCortadBuilding] = useState(false);

  const {
    bleachingYears,
    bleachingObservations,
    bleachingYear,
    setBleachingYear,
    bleachingLoading,
  } = useBleachingMapData(initialSettings.bleachingYear);

  const {
    expeditionTrackGeoJson,
    hasExpeditionTrack,
  } = useExpeditionTrack(projectId);

  const handleBleachingYearSelect = (year) => {
    setBleachingYear(year);
    setBleachingVisible(true);
  };

  const handleBleachingShowAll = () => {
    setBleachingYear(null);
    setBleachingVisible(true);
  };

  useEffect(() => {
    let cancelled = false;
    const loadCortad = async () => {
      try {
        const res = await fetch('/api/public/cortad/meta');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setCortadMeta(data);
        setCortadBuilding(Boolean(data.building));
        const months = data.variables?.[cortadVariable]?.months || [];
        setCortadMonths(months);
        if (months.length && !months.includes(cortadMonth)) {
          setCortadMonth(pickDefaultMonth(months));
        }
      } catch (_) {
        // Optional until bake finishes.
      }
    };
    loadCortad();
    const timer = window.setInterval(loadCortad, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [cortadVariable, cortadMonth]);

  const scopedColonies = useMemo(() => {
    if (!Array.isArray(colonies)) return [];
    return colonies.map((colony) => ({
      ...colony,
      projects: colony.projects?.length ? colony.projects : (projectName ? [projectName] : []),
    }));
  }, [colonies, projectName]);

  const globalMapLink = useMemo(
    () => buildGlobalMapLink({ colonies: scopedColonies, projectName, bleachingYear }),
    [scopedColonies, projectName, bleachingYear],
  );

  useEffect(() => {
    persistMapLayerSettings(PROJECT_MAP_SETTINGS_KEY, {
      basemap,
      captionsVisible,
      benthicVisible,
      benthicClasses,
      reefExtentVisible,
      reefExtentClasses,
      bleachingVisible,
      bleachingYear,
      expeditionTrackVisible,
      cortadVisible,
      cortadVariable,
      cortadMonth,
    });
  }, [
    basemap,
    captionsVisible,
    benthicVisible,
    benthicClasses,
    reefExtentVisible,
    reefExtentClasses,
    bleachingVisible,
    bleachingYear,
    expeditionTrackVisible,
    cortadVisible,
    cortadVariable,
    cortadMonth,
  ]);

  if (!scopedColonies.length) {
    return (
      <div className="project-map-panel project-map-panel-empty">
        <p className="text-muted mb-0">No colonies with coordinates for this project.</p>
      </div>
    );
  }

  return (
    <SidebarFilterProvider scopedColonies={scopedColonies}>
      <div className="project-map-panel">
        <div className="project-map-canvas">
          <Map
            basemap={basemap}
            captionsVisible={captionsVisible}
            benthicVisible={benthicVisible}
            benthicClasses={benthicClasses}
            reefExtentVisible={reefExtentVisible}
            reefExtentClasses={reefExtentClasses}
            bleachingVisible={bleachingVisible}
            bleachingYear={bleachingYear}
            bleachingObservationsGeoJson={bleachingObservations}
            onBleachingObservationClick={setSelectedBleachingObs}
            scopedColonies={scopedColonies}
            skipUrlFocus
            expeditionTrackGeoJson={expeditionTrackGeoJson}
            expeditionTrackVisible={hasExpeditionTrack && expeditionTrackVisible}
            expeditionTrackFitBounds={hasExpeditionTrack}
            colonyFitBounds={!hasExpeditionTrack}
            cortadVisible={cortadVisible}
            cortadVariable={cortadVariable}
            cortadMonth={cortadMonth}
            cortadMonths={cortadMonths}
            onCortadMonthChange={setCortadMonth}
            cortadMeta={cortadMeta}
          />
        </div>
        <div className="project-map-sidebar-wrap">
          <InputSidebar
            layout="embedded"
            sidebarTitle="Map & filters"
            hideProjectFilter
            basemap={basemap}
            onBasemapChange={setBasemap}
            captionsVisible={captionsVisible}
            onCaptionsVisibleChange={setCaptionsVisible}
            benthicVisible={benthicVisible}
            onBenthicVisibleChange={setBenthicVisible}
            benthicClasses={benthicClasses}
            onBenthicClassesChange={setBenthicClasses}
            reefExtentVisible={reefExtentVisible}
            onReefExtentVisibleChange={setReefExtentVisible}
            reefExtentClasses={reefExtentClasses}
            onReefExtentClassesChange={setReefExtentClasses}
            bleachingVisible={bleachingVisible}
            onBleachingVisibleChange={setBleachingVisible}
            bleachingYear={bleachingYear}
            onBleachingYearChange={setBleachingYear}
            bleachingYears={bleachingYears}
            expeditionTrackVisible={expeditionTrackVisible}
            onExpeditionTrackVisibleChange={setExpeditionTrackVisible}
            expeditionTrackAvailable={hasExpeditionTrack}
            cortadVisible={cortadVisible}
            onCortadVisibleChange={setCortadVisible}
            cortadVariable={cortadVariable}
            onCortadVariableChange={setCortadVariable}
            cortadMonth={cortadMonth}
            cortadMonths={cortadMonths}
            cortadBuilding={cortadBuilding}
          />
        </div>
      </div>
      <ProjectBleachingSummary
        colonies={scopedColonies}
        bleachingObservations={bleachingObservations}
        bleachingYear={bleachingYear}
        onSelectYear={handleBleachingYearSelect}
        onSelectAllYears={handleBleachingShowAll}
        loading={bleachingLoading}
      />
      <div className="project-map-footer">
        <Button
          as={Link}
          to={globalMapLink}
          variant="outline-primary"
          className="project-map-global-link"
        >
          <BoxArrowUpRight className="me-2" size={16} aria-hidden />
          Open on global map
        </Button>
      </div>
      <BleachingObservationModal
        show={!!selectedBleachingObs}
        onHide={() => setSelectedBleachingObs(null)}
        observation={selectedBleachingObs}
      />
    </SidebarFilterProvider>
  );
};

export default ProjectMapPanel;
